import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import { useNavigate } from 'react-router-dom';
import PaymentDialog from '../components/PaymentDialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Crown, Zap, CheckCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';
import { toast } from 'sonner';

export default function PaymentRequired() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPayment, setShowPayment] = useState(false);
  const [paymentType, setPaymentType] = useState('one_time');

  useEffect(() => {
    if (!user) {
      navigate('/');
    } else if (user.premium_status) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handlePaymentSuccess = async () => {
    await checkAuth();
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-8 w-8 text-teal-500" />
              <span className="text-2xl font-bold text-gray-900">Chronos</span>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600">
              Join the global time exchange community
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Premium Plan */}
            <Card className="p-8 border-2 border-teal-500 shadow-xl hover:shadow-2xl transition-shadow relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-teal-500 text-white px-4 py-1 text-sm font-semibold">
                Most Popular
              </div>
              <div className="text-center mb-6 mt-4">
                <Crown className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Premium</h2>
                <div className="text-5xl font-bold text-teal-600 mb-2">£1</div>
                <p className="text-gray-600 font-medium">One-time payment</p>
                <p className="text-sm text-gray-500 mt-2">No tricks, no hidden fees. Use forever.</p>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Unlimited time exchanges</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Real-time messaging & notifications</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Automatic translation (140+ languages)</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Browse & connect with community</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Reputation & trust system</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Premium badge</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">Lifetime access - pay once, use forever</span>
                </div>
              </div>

              <Button
                data-testid="select-premium-button"
                onClick={() => {
                  setPaymentType('one_time');
                  setShowPayment(true);
                }}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg"
              >
                Get Premium Now
              </Button>
            </Card>

            {/* Creator Plan */}
            <Card className="p-8 border-2 border-purple-500 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="text-center mb-6">
                <Zap className="h-16 w-16 mx-auto text-purple-500 mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Creator</h2>
                <div className="text-5xl font-bold text-purple-600 mb-2">£9.88</div>
                <p className="text-gray-600 font-medium">Per month</p>
                <p className="text-sm text-gray-500 mt-2">For teachers & course creators</p>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">All Premium features</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Create & host courses</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Group sessions & workshops</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Creator analytics dashboard</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Priority listing in browse</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Special Creator badge</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Monetize your skills</span>
                </div>
              </div>

              <Button
                data-testid="select-creator-button"
                onClick={() => {
                  setPaymentType('subscription');
                  setShowPayment(true);
                }}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg"
              >
                Become a Creator
              </Button>
            </Card>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-4">Secure payment powered by PayPal</p>
            <div className="flex items-center justify-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">Secure Checkout</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentDialog
        open={showPayment}
        onClose={() => setShowPayment(false)}
        onSuccess={handlePaymentSuccess}
        paymentType={paymentType}
      />
    </div>
  );
}